import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import {
  StoragePreSignUploadRequest,
  StoragePreSignDownloadRequest,
  StoragePresignedUrl,
  StorageFileInfo,
} from '@core/types/storage.type';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/storage`;

  getPreSignedUploadUrl(data: StoragePreSignUploadRequest): Observable<ApiResponse<StoragePresignedUrl>> {
    return this.http.post<ApiResponse<StoragePresignedUrl>>(`${this.baseUrl}/presign/upload`, data).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error generating upload URL');
      }),
    );
  }

  getPreSignedDownloadUrl(data: StoragePreSignDownloadRequest): Observable<ApiResponse<StoragePresignedUrl>> {
    return this.http.post<ApiResponse<StoragePresignedUrl>>(`${this.baseUrl}/presign/download`, data).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error generating download URL');
      }),
    );
  }

  deleteObject(data: StoragePreSignDownloadRequest): Observable<ApiResponse<void>> {
    return this.http.request<ApiResponse<void>>('DELETE', `${this.baseUrl}/object`, { body: data }).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error deleting object');
      }),
    );
  }

  listFiles(module: string, subFolder?: string, maxKeys?: number): Observable<ApiResponse<StorageFileInfo[]>> {
    let params = new HttpParams().set('module', module);
    if (subFolder) params = params.set('subFolder', subFolder);
    if (maxKeys !== undefined) params = params.set('maxKeys', String(maxKeys));

    return this.http.get<ApiResponse<StorageFileInfo[]>>(`${this.baseUrl}/list`, { params }).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message || 'Error listing files');
      }),
    );
  }

  /**
   * Sube el archivo directamente a S3 usando una URL firmada (PUT).
   * Nota: este método NO usa HttpClient porque el destino es externo (S3).
   */
  async uploadToPresignedUrl(url: string, file: File, contentType: string): Promise<void> {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`S3 upload failed (${res.status}): ${text}`);
    }
  }
}

